import React, { Component } from 'react';
import BeagleCardLibrary from 'beagle-card-library';
import './team-card.css';
import _ from 'lodash';
import { CONFIG } from '../../helper/config';
import { ChartConfigInfo } from '../../modal/filters';
import DisplayFilterComponent from '../display-filter-component/display-filter-component';
import { Menu, Image } from 'semantic-ui-react';
import { IMAGECONFIG } from '../../helper/image-config';
import { activeLoader } from '../../actions/view.actions';
import { connect } from 'react-redux';
import LoaderComponent from '../loader/loader';
import { updateTeamsDefaultView ,getCsv } from '../../actions/card-library.actions';
import {marked} from 'marked';

interface IProps {
  teams_filter: {};
  table_data: any;
  template_config: any;
  display_card_name?: boolean;
  is_update_chart_view_state: boolean;
  action_button_div_height?: number;
  teams_default_view: ChartConfigInfo;
  update_filter: boolean;
  updateChartViewState?: (is_update_chart_view_state: boolean) => void;
  updateTeamsDefaultView: (chart_config: ChartConfigInfo) => void;
}

interface IState {
  template_config: any;
  chart_view: boolean;
  chart_config: ChartConfigInfo;
  header_and_filter_div_height: number;
  note_overlay: boolean;
  loader: boolean;
}

/**
 * TeamsCard Component
 * 
 * A React component that renders a dynamic card view using the BeagleCardLibrary. 
 * The component allows switching between table and chart views, displays filters, 
 * chart options, notes, and footers, and integrates with Redux for managing 
 * the selected chart view and filter state.
 * 
 * ### Features:
 * - Displays table data or chart view based on the selected chart type.
 * - Supports multiple chart types with icons for switching between views.
 * - Renders filter data using the DisplayFilterComponent.
 * - Dynamically adjusts height based on static content (header, filters, notes, footer).
 * - Renders card notes with support for links and markdown formatting.
 * - Integrates with Redux to update and persist the selected chart view and filter state.
 * - Shows a loader when switching between chart/table views.
 * 
 * ### Props (IProps):
 * - `teams_filter: {}` – Filter configuration for teams data.
 * - `table_data: any` – Table data to be displayed in the card.
 * - `template_config: any` – Template configuration for the card (chart types, layout, etc.).
 * - `display_card_name?: boolean` – Whether to show the card name at the top.
 * - `is_update_chart_view_state: boolean` – Flag indicating if the chart view state should be updated.
 * - `action_button_div_height?: number` – Dynamic height adjustment for parent container spacing.
 * - `teams_default_view: ChartConfigInfo` – Default chart configuration stored in Redux.
 * - `update_filter: boolean` – Indicates if filters need to be updated from Redux.
 * - `updateChartViewState?: (is_update_chart_view_state: boolean) => void` – Callback to update chart view state in Redux.
 * - `updateTeamsDefaultView: (chart_config: ChartConfigInfo) => void` – Updates the default chart view in Redux.
 * 
 * ### State (IState):
 * - `template_config: any` – Local copy of the template configuration.
 * - `chart_view: boolean` – Whether the current view is a chart (true) or table (false).
 * - `chart_config: ChartConfigInfo` – Currently selected chart configuration.
 * - `header_and_filter_div_height: number` – Total static height of header, filter, notes, and footer sections.
 * - `note_overlay: boolean` – Whether the note overlay is visible.
 * - `loader: boolean` – Loader state for chart switching.
 * 
 */
export class TeamsCard extends Component<IProps, IState> {
  card_builder_container_ref: any = React.createRef();
  display_filter_ref: any = React.createRef();
  card_name_ref: any = React.createRef();
  card_chart_options_ref: any = React.createRef();
  card_footer_ref: any = React.createRef();
  card_filter_and_chart_options_ref: any = React.createRef();
  card_notes_ref: any = React.createRef();

  constructor(props: IProps) {
    super(props);
    let { template_config } = props;
    let chart_ =  template_config?.default_template_view?.chart_id ? template_config.default_template_view : CONFIG.CHARTTYPE.TABLE;
    this.state = {
      template_config: (template_config) ? JSON.parse(JSON.stringify(template_config)) : {},
      header_and_filter_div_height: 0,
      chart_view: this.isChartView(),
      chart_config: chart_,
      note_overlay: false,
      loader: false,
    }

    if (!_.isEqual(chart_.chart_id, props.teams_default_view.chart_id)) {
      this.props.updateTeamsDefaultView(chart_);
    }
  }

  /**
   * This function is called when the component is mounted. It updates the static content height of the card.
   */
  componentDidMount() {
    this.updateStaticContentHeight();
  }

  /**
   * Checks if the current chart view is a chart view or not.
   * @returns {boolean} True if the current chart view is a chart view, false otherwise.
   */
  isChartView = () => {
    let { template_config } = this.props;
    return  template_config?.default_template_view?.chart_id && template_config.default_template_view.chart_id !== CONFIG.CHARTTYPE.TABLE.chart_id;
  }

  /**
   * Updates the component state when the template_config prop is updated.
   * If is_update_chart_view_state is true, it updates the chart_config and chart_view state.
   * If is_update_chart_view_state is false, it updates the template_config and dispatches an action to update the chart view state.
   * @param {IProps} prevProps The previous props.
   */
  componentDidUpdate(prevProps: IProps) {
    let { template_config, is_update_chart_view_state, teams_default_view, update_filter } = this.props;
    if (!_.isEqual(prevProps.template_config, template_config)) {
      if (template_config?.id && !update_filter) {
        if (is_update_chart_view_state) {
          let chart_view = this.chartView(template_config);
          this.setState({
            template_config: JSON.parse(JSON.stringify(template_config)),
            chart_view: this.isChartView(),
            chart_config: chart_view
          })
          if (!_.isEqual(chart_view.chart_id, teams_default_view.chart_id)) {
            this.props.updateTeamsDefaultView(chart_view);
          }
        } else {
          this.setState({ template_config: JSON.parse(JSON.stringify(template_config)) });
          this.props.updateChartViewState!(true);
        }
      }
    }
    this.updateStaticContentHeight();
  }

  /**
   * Returns the chart view based on the template_config.
   * If the template_config has a default_template_view with a chart_id, it returns that.
   * Otherwise, it returns the default table chart type.
   * @param {any} template_config - The template configuration object.
   * @returns {any} The chart view object.
   */
  chartView = (template_config) => {
    return  template_config?.default_template_view?.chart_id ? template_config.default_template_view : CONFIG.CHARTTYPE.TABLE;
  }

  /**
   * Updates the static content height of the card. This function is called when the component is mounted and when the template_config prop is updated.
   * It calculates the height of the card name, footer, filter and chart options, and notes. It sets the header_and_filter_div_height state to the total height.
   */
  updateStaticContentHeight = () => {
    let { display_card_name } = this.props;
    let card_name = display_card_name &&  this.card_name_ref?.current ? this.card_name_ref.current.offsetHeight : 0;
    let card_footer = display_card_name &&  this.card_footer_ref.current ? this.card_footer_ref.current.offsetHeight : 0;
    let card_filter_and_chart_options =  this.card_filter_and_chart_options_ref?.current ? this.card_filter_and_chart_options_ref.current.offsetHeight : 0;
    let card_notes =  this.card_notes_ref?.current ? this.card_notes_ref.current.offsetHeight : 0;
    let height = card_filter_and_chart_options + card_name + card_footer + card_notes + 2;
    if (!isNaN(height) && Math.abs(this.state.header_and_filter_div_height - height) >= 1) {
      this.setState({ header_and_filter_div_height: height });
    }
  }

  /**
   * Updates the card view when the chart type is changed.
   * It updates the teams_default_view in the redux store and sets the chart_config and chart_view state.
   * @param {ChartConfigInfo} chart_type - The new chart type.
   */
  updateCardView = (chart_type: ChartConfigInfo) => {
    this.props.updateTeamsDefaultView(chart_type);
    this.setState({ loader: true, chart_config: chart_type, chart_view: chart_type.chart_id !== CONFIG.CHARTTYPE.TABLE.chart_id })
    setTimeout(() => this.setState({ loader: false }), 500);
  }

  /**
   * Toggles the note overlay state when the card notes are clicked.
   * If the note overlay is already open, it closes it. If the note overlay is closed,
   * it opens it if the target element's width is less than its scroll width.
   * @param {React.MouseEvent} event - The click event.
   */
  cardNoteClick = (event) => {
    let { note_overlay } = this.state;
    if (note_overlay || event.target.offsetWidth < event.target.scrollWidth) {
      this.setState({ note_overlay: !note_overlay });
    }
  }

  /**
   * Renders the card footer. This component is a div that displays the card footer.
   * The footer is displayed when the template_config and table_data props are not empty.
   * The footer has a click event that toggles the note overlay state.
   */
  renderCardFooter = () => {
    const { table_data, display_card_name } = this.props;
    const { template_config } = this.state;
    if (template_config && table_data.data) {
      return <div id={display_card_name ? 'view-card-footer' : 'deep-dive-card-footer'} className="card-text card-footer"
        style={{ color: 'black',display:'flex',flexDirection:'row',justifyContent:'space-between',width:'95%' }} ref={this.card_footer_ref} >
          <div>Powered By Beagle</div>
          {template_config?.last_updated_at?<div>Data as of : {template_config?.last_updated_at}</div>:null}
          </div>;
    } else {
      return null;
    }
  }

  /**
   * Renders the card notes. This component is a button that displays the card notes.
   * The button is displayed when the template_config.note prop is not empty.
   * The button has a click event that toggles the note overlay state.
   * The button also has a tooltip that displays the note text.
   */
  renderCardNotes = () => {
    const { template_config } = this.state;
    const renderer = {
      link(href,title, tokens) {

        return `<a target="_blank" href="${href}" title="${href}">${tokens}</a>`;
      }
    };

    /**
     * URL Tokenizer
     */
    const tokenizer = {
      url(src) {
        const match = src.match(/^(https?:\/\/[^\s<>()[\],:;"']+|www\.[^\s<>()[\],:;"']+|\b[A-Za-z0-9._+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b)/);
        if (match) {
          return {
            type: 'text', 
            raw: match[0],
            text: match[0]
          };
        }
        return false;
      }
    };


    marked.use({ renderer:renderer , tokenizer: tokenizer as any});

    if (template_config.note && template_config.note.trim() !== '') {
      return <div id='card-note-div' ref={this.card_notes_ref}>
        <button id='card-note-container' className={this.state.note_overlay ? 'floating-container' : 'notes-text'}
          style={{ cursor:  this.card_notes_ref?.current?.offsetWidth < this.card_notes_ref.current?.scrollWidth ? 'ponter' : 'default',  display:"flex",textAlign:"left" }}
          onClick={this.cardNoteClick} onKeyDown={this.cardNoteClick}>
          <b>{CONFIG.CARDNOTEHEADER}</b>
          <div id='note-message' dangerouslySetInnerHTML={{ __html: marked.parse(template_config.note.trim()) }} />
        </button>
      </div>
    } else {
      return null;
    }
  }
  
  /**
   * Renders the card filter and chart options. This component is a semantic UI
   * Menu component. The menu has a filter component and a chart options component.
   * The filter component is a DisplayFilterComponent. The chart options component
   * is a set of images that represent the different chart types. When an image is
   * clicked, the updateCardView function is called with the corresponding chart type.
   */
  rendercardFilterAndChartOptions = () => {
    const { teams_filter } = this.props;
    const { template_config, chart_config } = this.state;
    return <div ref={this.card_filter_and_chart_options_ref}>
      <Menu secondary >
        <DisplayFilterComponent ref={this.display_filter_ref}
         teams_filter={teams_filter} />

        <Menu.Menu position='right' >
          { template_config?.chart_config ?
            <div style={{ display: 'flex' }} ref={this.card_chart_options_ref}>
              {template_config.chart_config.map(chart => {
                let img_src = (chart_config.chart_id === chart.chart_id) ? IMAGECONFIG[`CHART${chart.chart_id}ICONBLUE`] : IMAGECONFIG[`CHART${chart.chart_id}ICON`];
                return <Image className='icon-images' key={chart.chart_id}
                  src={img_src}
                  onClick={() => this.updateCardView(chart)} />
              })}
            </div>
            : null}
        </Menu.Menu>
      </Menu>

    </div>
  }

  /**
   * Updates the selected filter for the given chart config.
   * 
   * @param {Array} selected_filter - The selected filter array.
   * @param {Object} chart_config - The chart config object.
   */
  updateChartFilters = (selected_filter: any[], chart_config: any) => {
    let { teams_default_view } = this.props;
    if (chart_config) {
      if (teams_default_view.chart_id === chart_config.chart_id) {
        if (teams_default_view.chart_options) {
          teams_default_view.chart_options.selected_filter = selected_filter;
        } else {
          teams_default_view.chart_options = { selected_filter: selected_filter };
        }
        this.props.updateTeamsDefaultView(teams_default_view)
      }
    }
  }

  /**
   * Updates the DC filter for the given chart config.
   * 
   * @param {string} dc_filter - The DC filter value.
   * @param {Object} chart_config - The chart config object.
   */
  updateChartDCFilter = (dc_filter: string, chart_config: any) => {
    let { teams_default_view } = this.props;
    if (chart_config) {
      if (teams_default_view.chart_id === chart_config.chart_id) {
        if (teams_default_view.chart_options) {
          teams_default_view.chart_options.dc_filter = dc_filter;
        } else {
          teams_default_view.chart_options = { dc_filter: dc_filter };
        }
        this.props.updateTeamsDefaultView(teams_default_view)
      }
    }
  }

  /**
   * Returns the chart options for the given chart config.
   * 
   * @param {Object} template_config - The template config object.
   * @param {Object} chart_config - The chart config object.
   * @returns {Object} The chart options object.
   */
  getChartOptions = (template_config: any, chart_config: ChartConfigInfo) => {
    let index = -1;
    if (template_config.chart_config) {
      index = template_config.chart_config ? template_config.chart_config.findIndex(cc => cc.chart_id === chart_config.chart_id) : -1;
      if (index !== -1 && template_config.chart_config[index].chart_options) {
        return JSON.parse(JSON.stringify(template_config.chart_config[index].chart_options));
      }
    }
    return null;
  }

  /**
   * Returns the client height of the card builder container.
   * 
   * @returns {number} The client height of the card builder container.
   */
  getClientHeight = () => {
    if (this.card_builder_container_ref?.current?.clientHeight) {
      return this.card_builder_container_ref.current.clientHeight
    }
  }

  /**
   * Renders the TeamsCard component. This component is a wrapper for the BeagleCardLibrary component.
   * It displays the card name, filter options, and chart options. It also handles the loading state
   * and displays the card notes and footer.
   */
  render() {
    const {
      table_data,
      teams_filter,
      display_card_name,
      action_button_div_height
    } = this.props;
    const { template_config, chart_config, chart_view, header_and_filter_div_height, loader } = this.state;
    return (
      <div className="card-table" style={{ height: `calc(100% - ${action_button_div_height ?? 0}px)` }} >
        {display_card_name ? <div className="card-text card-name" ref={this.card_name_ref}>
          {template_config.name || ''}
        </div> : null}

        {this.rendercardFilterAndChartOptions()}

        <div id="card-builder-container" ref={this.card_builder_container_ref} style={{ height: `calc(100% - ${header_and_filter_div_height}px)` }} >
          {template_config ? (
            <BeagleCardLibrary
              display_card_name={false}
              template={template_config.id}
              template_config={template_config}
              table_data={table_data}
              render_platform={'teams'}
              teams_filter={teams_filter}
              chart_view={chart_view}
              chart_config={{
                chart_id: chart_config.chart_id,
                chart_options: this.getChartOptions(template_config, chart_config),
                chart_name: chart_config.chart_name
              }}
              updateChartFilters={this.updateChartFilters}
              updateChartDCFilter={this.updateChartDCFilter}
              displayHeight={this.getClientHeight()}
            ></BeagleCardLibrary>
          ) : null}
          <LoaderComponent active_loader={loader} loading_time={0} />
        </div>
        {this.renderCardNotes()}
        {this.renderCardFooter()}
      </div>
    );
  }
}

/**
 * Maps state to props for the TeamsCard component.
 * Retrieves the teams_default_view and update_filter from the card library state.
 * @param {Object} state - The state of the application.
 * @returns {Object} A props object containing the teams_default_view and update_filter.
 */
const mapStateToProps = (state: any) => {
  const { teams_default_view, update_filter } = state.cardLibraryState;

  return {
    teams_default_view: teams_default_view,
    update_filter: update_filter
  }
}

export default connect(mapStateToProps, { activeLoader, updateTeamsDefaultView ,getCsv})(TeamsCard);
